/**
 * Direct-share links for the messengers Iranian partners use. Formats checked against the live
 * sites (2026-10-07): Telegram `t.me/share/url`, WhatsApp `wa.me/?text`, Bale `ble.ir/share/url`
 * (`ble.ir/share?text=` only redirects there), Eitaa `eitaa.com/share/url`. Rubika has no web
 * share address (`rubika.ir/share` is just its home page), so it is «copy the text and open
 * Rubika». On phones the system share sheet (`navigator.share`) covers the rest.
 */
import type { ShareChannelKey } from "@mfa-aff/shared";

export type ShareTarget = Exclude<ShareChannelKey, "copy" | "native">;

export type SharePayload = {
    /** The link to share; omitted when only a code is shared. */
    url?: string;
    /** The message around it. */
    text: string;
};

const enc = encodeURIComponent;

/** Text and link in one message (WhatsApp, Rubika, a code without a link). */
export const fullText = ({ url, text }: SharePayload): string => (url ? `${text}\n${url}` : text);

/** The address that opens the messenger with the message filled in; null = copy and open instead (Rubika). */
export function shareHref(target: ShareTarget, p: SharePayload): string | null {
    switch (target) {
        case "telegram":
            return p.url ? `https://t.me/share/url?url=${enc(p.url)}&text=${enc(p.text)}` : `https://t.me/share/url?url=${enc(p.text)}`;
        case "whatsapp":
            return `https://wa.me/?text=${enc(fullText(p))}`;
        case "bale":
            return `https://ble.ir/share/url?url=${enc(p.url ?? "")}&text=${enc(p.url ? p.text : fullText(p))}`;
        case "eitaa":
            return `https://eitaa.com/share/url?url=${enc(p.url ?? "")}&text=${enc(p.url ? p.text : fullText(p))}`;
        case "rubika":
            return null;
    }
}

/** Where «روبیکا» goes after the text is copied. */
export const RUBIKA_WEB = "https://web.rubika.ir/";

export const SHARE_TARGETS: readonly { key: ShareTarget; label: string; color: string }[] = [
    { key: "telegram", label: "تلگرام", color: "#229ED9" },
    { key: "whatsapp", label: "واتساپ", color: "#25D366" },
    { key: "bale", label: "بله", color: "#1FAF8C" },
    { key: "eitaa", label: "ایتا", color: "#F28C28" },
    { key: "rubika", label: "روبیکا", color: "#7A4CE0" },
];

/** The system share sheet exists (phones, some desktop browsers). */
export const canNativeShare = (): boolean => typeof navigator !== "undefined" && typeof navigator.share === "function";
